// src/routes/index.jsx
import React from "react";
import { AuthProvider, useAuth } from "@context/AuthContext";
import { MarketplaceProvider } from "@context/MarketplaceContext";
import {
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation
} from "react-router-dom";

import OCLayout from "@components/layout/OCLayout";

import OrdersPage from "@pages/Orders";
import OrderInfoPage from "@pages/Orders/OrderInfo";
import OrdersByDate from "@pages/Orders/OrdersByDate";
import OrdersOnMap from "@pages/Orders/OrdersOnMap";
import CleaningOrdersByTwoDays from "@pages/Orders/CleaningOrdersByTwoDays";
import FlaggedCleaningOrdersByTwoDays from "@pages/Orders/FlaggedCleaningOrdersByTwoDays";
import SewingCleaningOrdersByThreeDays from "@pages/Orders/SewingCleaningOrdersByThreeDays";
import CarpetsCleaningOrdersByTwoDays from "@pages/Orders/CarpetsCleaningOrdersByTwoDays";
import FurnitureCarCleaningOrdersByTwoDays from "@pages/Orders/FurnitureCarCleaningOrdersByTwoDays";

import CategoriesPage from "@pages/Catalog/CategoriesPage";
import CategoryEditPage from "@pages/Catalog/CategoryEditPage";
import ProductsPage from "@pages/Catalog/ProductsPage";
import ProductEditPage from "@pages/Catalog/ProductEditPage";

import CustomersPage from "@pages/customers/CustomersPage";
import CustomerGroupsPage from "@pages/customers/CustomerGroupsPage";
import CustomerFeedbackPage from "@pages/customers/CustomerFeedbackPage";
import CustomerEditPage from "@pages/customers/CustomerEditPage";

import LandingPagePage from "@pages/Catalog/LandingPagePage";
import OrderTypeDescriptionPage from "@pages/Orders/OrderTypeDescription";

import CouponsPage from "@pages/Marketing/CouponsPage";
import CouponOrdersPage from "@pages/Marketing/CouponOrdersPage";
import TimeSlotsPage from "@pages/Logistics/TimeSlotsPage";
import TimeSlotsSettingsPage from "@pages/Logistics/TimeSlotsSettingsPage";
import OrderSortingPage from "@pages/Logistics/OrderSortingPage";
import FranchisePage from "@pages/System/FranchisePage";
import StorePage from "@pages/System/StorePage";
import UserPage from "@pages/System/UserPage";
import UserGroupPage from "@pages/System/UserGroupPage";
import SmsMessagesPage from "@pages/System/SmsMessagesPage";
import AskAiPage from "@pages/Analytics/AskAiPage";

import NotificationPage from "@pages/Notifications/NotificationPage";
import ReportsPage from "@pages/Reports/ReportsPage";
import LoginPage from "@pages/Auth/LoginPage";

import SubscriptionPlansPage from "@pages/Website/SubscriptionPlansPage";
import SubscriptionsPage from "@pages/Website/SubscriptionsPage";
import ServiceSchedulingPage from "@pages/Website/ServiceSchedulingPage";
import ProductVariantsPage from "@pages/Website/ProductVariantsPage";
import ProductContentPage from "@pages/Website/ProductContentPage";
import ProductPlacementsPage from "@pages/Website/ProductPlacementsPage";
import B2bLeadsPage from "@pages/Website/B2bLeadsPage";

const Placeholder = ({ title }) => (
  <div className="oc-content">
    <div className="oc-card">
      <h2>{title}</h2>
    </div>
  </div>
);

function ProtectedLayout() {
  const auth = useAuth();
  const location = useLocation();

  if (!auth.user) {
//    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return (
    <OCLayout>
      <Outlet />
    </OCLayout>
  );
}

export default function AppRoutes() {
  return (
    <AuthProvider>
      <MarketplaceProvider>
        <Routes>
        <Route path="/login" element={<LoginPage />} />

        <Route element={<ProtectedLayout />}>
          <Route path="/" element={<Navigate to="/orders" replace />} />

          <Route path="/analytics/ask-ai" element={<AskAiPage />} />

          <Route path="/orders" element={<OrdersPage />} />
          <Route path="/orders/:id" element={<OrderInfoPage />} />

          <Route path="/dashboard" element={<Placeholder title="Dashboard" />} />

          <Route path="/catalog" element={<CategoriesPage />} />
          <Route path="/catalog/categories/:id" element={<CategoryEditPage />} />
        <Route path="/catalog/categories/new" element={<CategoryEditPage />} />

          <Route path="/catalog/products" element={<ProductsPage />} />
          <Route path="/catalog/products/:id" element={<ProductEditPage />} />

          <Route path="/extensions" element={<Placeholder title="Extensions" />} />
          <Route path="/design" element={<Placeholder title="Design" />} />

          <Route path="/sales/by-day" element={<OrdersByDate />} />
          <Route path="/sales/map" element={<OrdersOnMap />} />
          <Route
            path="/sales/cleaning-orders-by-two-days"
            element={<CleaningOrdersByTwoDays />}
          />
          <Route
            path="/sales/carpets-cleaning-orders-by-two-days"
            element={<CarpetsCleaningOrdersByTwoDays />}
          />

          <Route
            path="/sales/furniture-car-cleaning-orders-by-two-days"
            element={<FurnitureCarCleaningOrdersByTwoDays />}
          />

          <Route
            path="/sales/flagged-cleaning-orders-by-two-days"
            element={<FlaggedCleaningOrdersByTwoDays />}
          />
          <Route
            path="/sales/sewing-cleaning-orders-by-three-days"
            element={<SewingCleaningOrdersByThreeDays />}
          />

          <Route path="/sales/map" element={<Placeholder title="Orders on Map" />} />
          <Route
            path="/sales/reschedule"
            element={<Placeholder title="Reschedule Orders" />}
          />

          <Route path="/logistics" element={<Placeholder title="Logistics" />} />

          <Route path="/customers" element={<CustomersPage />} />
          <Route path="/customer-groups" element={<CustomerGroupsPage />} />
          <Route path="/customer-feedback" element={<CustomerFeedbackPage />} />
          <Route path="/customers/:id" element={<CustomerEditPage />} />
          <Route path="/customers/new" element={<CustomerEditPage />} />

          <Route path="/landing-page" element={<LandingPagePage />} />
          <Route
            path="/order-type-description"
            element={<OrderTypeDescriptionPage />}
          />

          <Route path="/marketing/coupons" element={<CouponsPage />} />
          <Route
            path="/marketing/coupons/:couponId/orders"
            element={<CouponOrdersPage />}
          />

          <Route path="/logistics/time-slots" element={<TimeSlotsPage />} />
          <Route path="/logistics/sorting" element={<OrderSortingPage />} />
          <Route
            path="/logistics/time-slots-settings"
            element={<TimeSlotsSettingsPage />}
          />

          <Route path="/system/franchise" element={<FranchisePage />} />
          <Route path="/system/store" element={<StorePage />} />
          <Route path="/system/user" element={<UserPage />} />
          <Route path="/system/user-group" element={<UserGroupPage />} />
          <Route path="/system/sms-messages" element={<SmsMessagesPage />} />

          <Route path="/notifications/notification" element={<NotificationPage />} />

          <Route path="/reports" element={<ReportsPage />} />

          <Route path="/website/subscription-plans" element={<SubscriptionPlansPage />} />
          <Route path="/website/subscriptions" element={<SubscriptionsPage />} />
          <Route path="/website/service-scheduling" element={<ServiceSchedulingPage />} />
          <Route path="/website/product-variants" element={<ProductVariantsPage />} />
          <Route path="/website/product-content" element={<ProductContentPage />} />
          <Route path="/website/product-placement" element={<ProductPlacementsPage />} />
          <Route path="/website/b2b-leads" element={<B2bLeadsPage />} />
        </Route>
        </Routes>
      </MarketplaceProvider>
    </AuthProvider>
  );
}

